import React, { useState } from 'react';

import { Button, Flex, Loader, Text, Tooltip } from '@mantine/core';
import { IconWorldUp, IconWorldX } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { IntegrationManagerService } from 'services/integration-manager-service';
import { NotificationService } from 'services/notification-service';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface AddSmartAssistantToStoreButtonProps {
  smartAssistant: SmartAssistant;
  menuItem?: boolean;
  disabled?: boolean;
}

export function AddSmartAssistantToStoreButton({
  smartAssistant,
  menuItem = false,
  disabled = false
}: AddSmartAssistantToStoreButtonProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isAddedToStore, setIsAddedToStore] = useState(!!smartAssistant.websiteIntegration);

  const toggleShopifyWebsiteIntegration = useMutation({
    mutationFn: IntegrationManagerService.toggleShopifyWebsiteIntegration,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      setIsAddedToStore(!!updatedSmartAssistant.websiteIntegration);
      NotificationService.showResult(true, 'FEED_BACK.SMART_ASSISTANT.SUCCESS.UPDATE_INTEGRATIONS');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.UPDATE_INTEGRATIONS')
  });

  const onButtonClick = () => toggleShopifyWebsiteIntegration.mutate(smartAssistant._id);

  if (menuItem) {
    return (
      <Flex
        align="center"
        gap={10}
        p="xs"
        sx={(theme) => ({
          borderRadius: theme.radius.sm,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.colors.gray[0]
          }
        })}
        onClick={() => !toggleShopifyWebsiteIntegration.isLoading && onButtonClick()}>
        {toggleShopifyWebsiteIntegration.isLoading ? (
          <Loader size="xs" />
        ) : isAddedToStore ? (
          <IconWorldX size={16} />
        ) : (
          <IconWorldUp size={16} />
        )}
        <Text size="sm">
          {isAddedToStore ? t('SMART_ASSISTANT.REMOVE_FROM_STORE') : t('SMART_ASSISTANT.ADD_TO_STORE')}
        </Text>
      </Flex>
    );
  }

  return (
    <Tooltip disabled={isAddedToStore} multiline maw={300} label={t('SMART_ASSISTANT.STORE_EMBED_INFO')}>
      <Button
        variant="outline"
        disabled={disabled}
        onClick={onButtonClick}
        loading={toggleShopifyWebsiteIntegration.isLoading}
        leftIcon={isAddedToStore ? <IconWorldX size={16} /> : <IconWorldUp size={16} />}>
        {isAddedToStore ? t('SMART_ASSISTANT.REMOVE_FROM_STORE') : t('SMART_ASSISTANT.ADD_TO_STORE')}
      </Button>
    </Tooltip>
  );
}
